// src/utils/api.js
const hardcoded = "https://optimizer-server-v1.onrender.com";

const fromEnv =
  typeof process !== "undefined" &&
  process.env &&
  process.env.REACT_APP_API_BASE_URL
    ? process.env.REACT_APP_API_BASE_URL
    : null;

// If you ever change domains, change this in one place.
const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  fromEnv ||
  hardcoded;

export default API_BASE;

/* ------------ small helpers so pages don't repeat fetch logic ----------- */
export async function postJSON(url, body, opts = {}) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    body: JSON.stringify(body),
  });
  return res;
}

/**
 * Probe Cup endpoints in order, returning the first one that responds with a non-404.
 * If streaming==true, we try the *_stream variants first.
 * Always includes { series: "cup" } in the payload for generic endpoints.
 */
export async function probeAndPostCup(payload, { streaming = false } = {}) {
  const p = { ...payload, series: payload.series || "cup" };

  // Try most specific → least specific
  const paths = streaming
    ? ["/cup/solve_stream", "/nascar/cup/solve_stream", "/solve_stream", "/solve"]
    : ["/cup/solve", "/nascar/cup/solve", "/solve"];

  // Try each path until we get something that isn't a 404
  for (const path of paths) {
    const url = `${API_BASE}${path}`;
    try {
      const res = await postJSON(url, p);
      if (res.status !== 404) {
        // Found a working path
        return { url, res };
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[Cup probe] ${path} -> 404`);
      }
    } catch (e) {
      // Network or CORS—log and keep probing
      // eslint-disable-next-line no-console
      console.warn(`[Cup probe] ${path} -> ${e}`);
    }
  }
  throw new Error("No working Cup endpoint found.");
}

/* ========================= CONTEST SIM HELPERS =========================
   These DO NOT change anything else; they just probe likely paths for
   Cup/Xfinity/Trucks and post your payload.
   - payouts preview (non-stream)
   - Monte Carlo simulate (stream or non-stream)
   Usage in pages:
     await postCupPayoutsPreview({...});
     const res = await postCupContestSim(payload, { streaming: true });
*/

async function probeAndPostGeneric(series, payload, paths) {
  const p = { series: series || "cup", ...payload };
  for (const path of paths) {
    const url = `${API_BASE}${path}`;
    try {
      const res = await postJSON(url, p);
      if (res.status !== 404) return { url, res };
      // eslint-disable-next-line no-console
      console.warn(`[Sim probe] ${path} -> 404`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Sim probe] ${path} -> ${e}`);
    }
  }
  throw new Error(`No working contest-sim endpoint found for ${series}.`);
}

export async function postContestPayoutsPreview(series, payload) {
  // Most specific → least specific; supports both styles we discussed
  const paths = [
    `/${series}/contest_payouts_preview`,
    `/nascar/${series}/contest_payouts_preview`,
    `/${series}/contest/payouts_preview`,
    `/contest_payouts_preview`,
    `/contest/payouts_preview`,
  ];
  const { res } = await probeAndPostGeneric(series, payload, paths);
  return res;
}

export async function postContestSim(series, payload, { streaming = false } = {}) {
  const p = { series, ...payload };
  const paths = streaming
    ? [
        `/${series}/contest_sim_stream`,
        `/nascar/${series}/contest_sim_stream`,
        `/${series}/contest_simulate_stream`,
        `/nascar/${series}/contest_simulate_stream`,
        `/contest_sim_stream`,
        `/contest_simulate_stream`,
      ]
    : [
        `/${series}/contest_sim`,
        `/nascar/${series}/contest_sim`,
        `/${series}/contest_simulate`,
        `/nascar/${series}/contest_simulate`,
        `/contest_sim`,
        `/contest_simulate`,
      ];
  const { res } = await probeAndPostGeneric(series, p, paths);
  return res;
}

/* ---------- convenient aliases so pages stay clean ---------- */
export const postCupPayoutsPreview = (payload) =>
  postContestPayoutsPreview("cup", payload);
export const postXfPayoutsPreview = (payload) =>
  postContestPayoutsPreview("xfinity", payload);
export const postTrucksPayoutsPreview = (payload) =>
  postContestPayoutsPreview("trucks", payload);

export const postCupContestSim = (payload, opts) =>
  postContestSim("cup", payload, opts);
export const postXfContestSim = (payload, opts) =>
  postContestSim("xfinity", payload, opts);
export const postTrucksContestSim = (payload, opts) =>
  postContestSim("trucks", payload, opts);

/* ---------- optional: tiny SSE reader if you want streaming ---------- */
export async function readEventStream(response, onEvent) {
  if (!response.body) throw new Error("No readable stream on response.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const chunk = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 2);
      if (!chunk) continue;
      // simple "data: {...}" parser
      const line = chunk.split("\n").find((l) => l.startsWith("data:"));
      if (line) {
        try {
          const payload = JSON.parse(line.replace(/^data:\s*/, ""));
          onEvent?.(payload);
        } catch {
          // ignore parse errors
        }
      }
    }
  }
}
